// Control scheme configuration — keys & mouse only

export type ControlScheme = 'keys_mouse';

// Actions that can be rebound
export type BindableAction =
  | 'accelerate'
  | 'reverse'
  | 'brake'
  | 'boost'
  | 'strafeLeft'
  | 'strafeRight'
  | 'rotateLeft'
  | 'rotateRight'
  | 'primaryAction'
  | 'secondaryAction'
  | 'interact'
  | 'toggleMap'
  | 'fastTravel'
  | 'follow'
  | 'radar';

// Human-readable names for actions
export const ACTION_NAMES: Record<BindableAction, string> = {
  accelerate: 'Accelerate',
  reverse: 'Reverse',
  brake: 'Brake',
  boost: 'Boost',
  strafeLeft: 'Strafe Left',
  strafeRight: 'Strafe Right',
  rotateLeft: 'Rotate Left',
  rotateRight: 'Rotate Right',
  primaryAction: 'Primary Action',
  secondaryAction: 'Secondary Action',
  interact: 'Interact',
  toggleMap: 'Constellation Map',
  fastTravel: 'Approach',
  follow: 'Follow',
  radar: 'Radar Scan',
};

// Actions that are keyboard-only and can be rebound
export const REBINDABLE_ACTIONS: BindableAction[] = [
  'accelerate',
  'reverse',
  'brake',
  'boost',
  'strafeLeft',
  'strafeRight',
  'rotateLeft',
  'rotateRight',
  'primaryAction',
  'secondaryAction',
  'interact',
  'toggleMap',
  'fastTravel',
  'follow',
  'radar',
];

export interface ControlSchemeConfig {
  name: string;
  description: string;
  bindings: {
    // Movement
    accelerate: string[];
    reverse: string[];
    brake: string[];
    boost: string[]; // Speed boost (requires booster equipment)
    strafeLeft: string[];
    strafeRight: string[];
    rotateLeft: string[] | 'mouse'; // 'mouse' means mouse controls rotation
    rotateRight: string[] | 'mouse';
    // Actions
    primaryAction: string[] | 'mouse_left';
    secondaryAction: string[] | 'mouse_right';
    interact: string[]; // Context-sensitive action (dock, interact with objects)
    radar: string[]; // Radar scan (requires sensor equipment)
    // UI
    toggleMap: string[];
    fastTravel: string[]; // Autopilot one-shot to waypoint/selected target
    follow: string[];     // Continuous autopilot that tracks a moving target
  };
}

// Custom bindings - maps action to a single key
export type CustomBindings = Partial<Record<BindableAction, string>>;

export const CONTROL_SCHEMES: Record<ControlScheme, ControlSchemeConfig> = {
  keys_mouse: {
    name: 'Keys & Mouse',
    description: 'Mouse aims, WASD moves, click to shoot',
    bindings: {
      accelerate: ['W'],
      reverse: ['S'],
      brake: ['SPACE'],
      boost: ['SHIFT'],
      strafeLeft: ['A'],
      strafeRight: ['D'],
      rotateLeft: 'mouse',
      rotateRight: 'mouse',
      primaryAction: 'mouse_left',
      secondaryAction: 'mouse_right',
      interact: ['E'],
      radar: ['R'],
      toggleMap: ['M'],
      fastTravel: ['F'],
      follow: ['X'],
    },
  },
};

// LocalStorage key for saving control preference
const CONTROL_SCHEME_STORAGE_KEY = 'astra_control_scheme';

// Get saved control scheme or default
export function getControlScheme(): ControlScheme {
  return 'keys_mouse';
}

// Save control scheme preference
export function setControlScheme(scheme: ControlScheme): void {
  try {
    localStorage.setItem(CONTROL_SCHEME_STORAGE_KEY, scheme);
  } catch {
    // localStorage might not be available
  }
}

// LocalStorage key for custom bindings
const CUSTOM_BINDINGS_STORAGE_KEY = 'astra_custom_bindings';

// Get saved custom bindings
export function getCustomBindings(): CustomBindings {
  try {
    const saved = localStorage.getItem(CUSTOM_BINDINGS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as CustomBindings;
    }
  } catch {
    // localStorage might not be available or invalid JSON
  }
  return {};
}

// Save a single custom binding
export function setCustomBinding(action: BindableAction, key: string): void {
  try {
    const bindings = getCustomBindings();
    bindings[action] = key;
    localStorage.setItem(CUSTOM_BINDINGS_STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // localStorage might not be available
  }
}

// Clear a custom binding (revert to default)
export function clearCustomBinding(action: BindableAction): void {
  try {
    const bindings = getCustomBindings();
    delete bindings[action];
    localStorage.setItem(CUSTOM_BINDINGS_STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // localStorage might not be available
  }
}

// Reset all custom bindings
export function resetAllCustomBindings(): void {
  try {
    localStorage.removeItem(CUSTOM_BINDINGS_STORAGE_KEY);
  } catch {
    // localStorage might not be available
  }
}

// Get the effective key for an action (custom or default)
export function getEffectiveBinding(action: BindableAction, scheme: ControlScheme): string | 'mouse' | 'mouse_left' | 'mouse_right' {
  const customBindings = getCustomBindings();
  const defaultBinding = CONTROL_SCHEMES[scheme].bindings[action];

  // If there's a custom binding, use it
  if (customBindings[action]) {
    return customBindings[action]!;
  }

  // Otherwise return the first default key or the special mouse value
  if (Array.isArray(defaultBinding)) {
    return defaultBinding[0];
  }
  return defaultBinding;
}

// Get all effective bindings for a scheme
export function getEffectiveBindings(scheme: ControlScheme): Record<BindableAction, string | 'mouse' | 'mouse_left' | 'mouse_right'> {
  const result: Record<string, string | 'mouse' | 'mouse_left' | 'mouse_right'> = {} as Record<BindableAction, string | 'mouse' | 'mouse_left' | 'mouse_right'>;
  for (const action of REBINDABLE_ACTIONS) {
    result[action] = getEffectiveBinding(action, scheme);
  }
  return result as Record<BindableAction, string | 'mouse' | 'mouse_left' | 'mouse_right'>;
}

// Check if an action uses mouse (not rebindable to keyboard)
export function isMouseAction(action: BindableAction, scheme: ControlScheme): boolean {
  const binding = CONTROL_SCHEMES[scheme].bindings[action];
  return binding === 'mouse' || binding === 'mouse_left' || binding === 'mouse_right';
}

// Get display name for a key
export function getKeyDisplayName(key: string): string {
  const displayNames: Record<string, string> = {
    'UP': '↑',
    'DOWN': '↓',
    'LEFT': '←',
    'RIGHT': '→',
    'SPACE': 'Space',
    'SHIFT': 'Shift',
    'CTRL': 'Ctrl',
    'ALT': 'Alt',
    'TAB': 'Tab',
    'ENTER': 'Enter',
    'ESC': 'Esc',
    'mouse': 'Mouse',
    'mouse_left': 'Left Click',
    'mouse_right': 'Right Click',
  };
  return displayNames[key] || key;
}
